import type { Middleware } from "@reduxjs/toolkit";
import { isAnyOf } from "@reduxjs/toolkit";

import type {
  NautilusDesignerState,
  NodeDesignsReducer,
  NodeDesignsState,
  ReferencedValue,
  StateAction,
  ValueTombstone,
  UpdateNodeConfigAction,
  UpdateConditionConfigAction,
} from "@skilitics/nautilus-core-designer-data";
import {
  findLockedNodeValueRefs,
  InvalidActionError,
  nodeDesignsReducerCreate,
  removeNodeAction,
  replaceNodeAction,
  replacementRepointsReferences,
  toValueTombstone,
  tombstoneLockedValueRefs,
  updateConditionAction,
  updateNodeConfigAction,
  valueReference,
} from "@skilitics/nautilus-core-designer-data";

import type { NautilusDesignerAppConfig } from "../../../state/index.ts";
import { resolveValueSignatureSelector } from "../../../values/valueSelectors.ts";

type DesignerState = NautilusDesignerState<NautilusDesignerAppConfig>;

/**
 * The only two actions the node designs reducer takes nodes away for. Every other
 * node action leaves `designsById` as it found it, and trigger actions never reach
 * it at all — so the dry run below is worth its cost for these alone.
 */
const takesNodesAway = isAnyOf(removeNodeAction, replaceNodeAction);

/**
 * Which nodes this action takes away — asked of the reducer rather than worked
 * out here, by applying it to a copy and seeing what is left. Which of a subtree's
 * residents and placeholders go, and whether a replaced node goes at all, is the
 * reducer's to say; a second reading of those rules would be a second place for
 * them to drift.
 *
 * An action the dry run rejects is left to be rejected for real a moment later.
 */
const removedNodeIds = (
  dryRun: NodeDesignsReducer,
  state: NodeDesignsState,
  action: Parameters<NodeDesignsReducer>[1],
): Set<number> => {
  let remaining;
  try {
    remaining = dryRun(state, action);
  } catch {
    return new Set();
  }
  if (remaining === state) return new Set();

  const surviving = remaining.designsById;
  return new Set(
    Object.keys(state.designsById)
      .filter((id) => !(id in surviving))
      .map(Number),
  );
};

/**
 * The tombstone each found reference becomes, read off the value its node still
 * advertises. A locked reference the designer cannot resolve a signature for is a
 * coding error rather than an authoring state — the lock says this reference is
 * the type authority for its slot — so the removal is rejected outright rather
 * than losing the expression's type quietly.
 */
const tombstonesFor = (state: DesignerState, references: ReferencedValue[], action: StateAction) => {
  const resolveValueSignature = resolveValueSignatureSelector(state);
  const tombstones = new Map<ReferencedValue, ValueTombstone>();

  for (const reference of references) {
    const signature = resolveValueSignature(reference);
    if (!signature) {
      const stored = valueReference(reference);
      throw new InvalidActionError(
        action,
        `No value signature behind the locked reference ${JSON.stringify(stored)}, so it cannot be tombstoned.`,
      );
    }
    tombstones.set(reference, toValueTombstone(signature));
  }

  return (reference: ReferencedValue) => tombstones.get(reference);
};

const hasReferences = <T extends { references: ReferencedValue[] }>(candidate: T) => candidate.references.length > 0;

/**
 * The writes that carry each locked reference's signature forward, dispatched
 * before the removal reaches the reducer: a tombstone is no longer a node
 * reference, so the reducer's own clearing and repointing pass over it, and
 * unlocked references keep clearing exactly as they always have.
 */
const generateTombstoneWriteActions = (
  state: Readonly<DesignerState>,
  doomedNodeIds: ReadonlySet<number>,
  action: StateAction,
): (UpdateNodeConfigAction | UpdateConditionConfigAction)[] => {
  const isDoomed = (nodeId: number) => doomedNodeIds.has(nodeId);
  const { designsById, triggerDesigns } = state.nodeDesigns;

  // a node config holds every value it points at under `values`; a condition's
  // build config holds them under whatever keys its builder chose
  const nodes = Object.entries(designsById)
    .filter(([id]) => !isDoomed(Number(id)))
    .map(([id, { config }]) => ({
      id: Number(id),
      config,
      references: findLockedNodeValueRefs(config.values, isDoomed),
    }))
    .filter(hasReferences);

  const conditions = Object.entries(triggerDesigns.conditions.byId)
    .filter(([, { nodeId }]) => !nodeId || !isDoomed(nodeId))
    .map(([id, { config }]) => ({ id: Number(id), config, references: findLockedNodeValueRefs(config, isDoomed) }))
    .filter(hasReferences);

  if (nodes.length === 0 && conditions.length === 0) return [];

  const toTombstone = tombstonesFor(
    state,
    [...nodes, ...conditions].flatMap(({ references }) => references),
    action,
  );

  return [
    ...nodes.map(({ id, config }) =>
      updateNodeConfigAction({
        id,
        config: { ...config, values: tombstoneLockedValueRefs(config.values, toTombstone) },
      }),
    ),
    ...conditions.map(({ id, config }) =>
      updateConditionAction({ id, config: tombstoneLockedValueRefs(config, toTombstone) }),
    ),
  ];
};

/**
 * Tombstones the locked references into a node about to go, so an expression over
 * a removed subject keeps the signature it was authored against — the repair
 * criteria for re-binding — instead of clearing to nothing.
 *
 * Middleware rather than a node-designs interceptor because the signature comes
 * from the removed node's value descriptors, and reading those stands on the app
 * config: the manifests, the root parent context, and the active content
 * language, none of which an interceptor sees.
 */
export const lockedValueRefTombstoneMiddlewareCreate = (): Middleware<{}, DesignerState> => {
  const dryRun = nodeDesignsReducerCreate();

  return (store) => (next) => (action) => {
    if (!takesNodesAway(action)) return next(action);

    const state = store.getState();
    const removed = removedNodeIds(dryRun, state.nodeDesigns, action);
    // a replacement that stands in for the old node repoints references rather than clearing them
    if (replaceNodeAction.match(action) && replacementRepointsReferences(action.payload)) {
      removed.delete(action.payload.id);
    }
    if (removed.size === 0) return next(action);

    for (const writeAction of generateTombstoneWriteActions(state, removed, action)) {
      store.dispatch(writeAction);
    }

    return next(action);
  };
};
